import React, { useCallback, useEffect, useMemo, useState } from "react";
import { backendUrl } from "../../../api/constants";
import axiosInstance from "../../../api/axiosInstance";
import Button from "../../../components/Button/Button";
import { useMediaQuery } from "../../../util/useMediaQuery";
import { usePortalContext } from "../../../context/PortalContext/portalcontext";
import { modal } from "../../../util/useModal";
import { PortalTitle } from "../../Portal";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  expandPostedEventsForCalendar,
  getCalendarStatusClass,
} from "../../../components/AppCalendar/calendarUtils";
import { getEventApplicants } from "../../../api/opportunities";
import { getSlotSummaryForOccurrence } from "../../../util/slotOccurrences";
import PostedEventsCalendar from "./PostedEventsCalendar";
import PostedEventsListDrawer from "./PostedEventsListDrawer";


const STATUS_FILTERS = [
  { id: "active", label: "active" },
  { id: "awaiting-review", label: "awaiting review" },
  { id: "rejected", label: "rejected" },
];

const LINEUP_FILTERS = [
  { id: "has-slots", label: "has slots" },
  { id: "open-slots", label: "open slots" },
  { id: "filled-slots", label: "filled slots" },
  { id: "pending-applicants", label: "pending applicants" },
];

const DEFAULT_STATUS_FILTERS = STATUS_FILTERS.map((status) => status.id);
const DEFAULT_CALENDAR_VIEW = "dayGridMonth";
const DEFAULT_VIEW_MODE = "calendar";
const DEFAULT_CONDENSE_REJECTED_RECURRING = true;
const POSTED_EVENTS_CALENDAR_PREFS_KEY = "posted_events_calendar_preferences";
const CALENDAR_VIEW_TYPES = [
  "dayGridMonth",
  "timeGridWeek",
  "timeGridDay",
  "listWeek",
];

const getPostedEventsCalendarPreferences = (storageKey = POSTED_EVENTS_CALENDAR_PREFS_KEY) => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(storageKey) || "{}"
    );

    return {
      viewMode: ["list", "calendar"].includes(parsed.viewMode)
        ? parsed.viewMode
        : DEFAULT_VIEW_MODE,
      calendarView: CALENDAR_VIEW_TYPES.includes(parsed.calendarView)
        ? parsed.calendarView
        : DEFAULT_CALENDAR_VIEW,
      enabledStatuses: Array.isArray(parsed.enabledStatuses)
        ? parsed.enabledStatuses.filter((status) =>
            DEFAULT_STATUS_FILTERS.includes(status)
          )
        : DEFAULT_STATUS_FILTERS,
      enabledLineupFilters: Array.isArray(parsed.enabledLineupFilters)
        ? parsed.enabledLineupFilters.filter((filter) =>
            LINEUP_FILTERS.some((lineupFilter) => lineupFilter.id === filter)
          )
        : [],
      condenseRejectedRecurring:
        typeof parsed.condenseRejectedRecurring === "boolean"
          ? parsed.condenseRejectedRecurring
          : DEFAULT_CONDENSE_REJECTED_RECURRING,
      filtersCollapsed:
        typeof parsed.filtersCollapsed === "boolean"
          ? parsed.filtersCollapsed
          : null,
    };
  } catch {
    return {
      viewMode: DEFAULT_VIEW_MODE,
      calendarView: DEFAULT_CALENDAR_VIEW,
      enabledStatuses: DEFAULT_STATUS_FILTERS,
      enabledLineupFilters: [],
      condenseRejectedRecurring: DEFAULT_CONDENSE_REJECTED_RECURRING,
      filtersCollapsed: null,
    };
  }
};

const applicantSummaryKey = (eventId, occurrenceStart) => {
  if (!eventId) return null;
  if (!occurrenceStart) return String(eventId);
  return `${eventId}:${new Date(occurrenceStart).getTime()}`;
};

const getSlotSummary = (event, occurrenceStart) =>
  getSlotSummaryForOccurrence(event, occurrenceStart);

const summarizeApplicants = (slots = []) => {
  const applications = slots.flatMap((slot) => slot.applications || []);

  return {
    slotDetails: slots,
    totalApplicants: applications.length,
    pendingApplicants: applications.filter((app) => app.status === "pending").length,
    acceptedApplicants: applications.filter((app) => app.status === "accepted").length,
  };
};

const eventMatchesSearch = (event, searchTerm) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return [event?.name, event?.venue?.name, event?.address?.name]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedSearch));
};

const eventMatchesLineupFilters = ({
  event,
  enabledLineupFilters,
  applicantSummary,
}) => {
  if (enabledLineupFilters.length === 0) return true;

  const slotSummary = getSlotSummary(event);
  const filterChecks = {
    "has-slots": slotSummary.hasSlots,
    "open-slots": slotSummary.openSlotCount > 0,
    "filled-slots": slotSummary.filledSlotCount > 0,
    "pending-applicants": applicantSummary
      ? applicantSummary.pendingApplicants > 0
      : slotSummary.hasSlots,
  };

  return enabledLineupFilters.every((filterId) => filterChecks[filterId]);
};

const filterPostedEventsForCalendar = (
  events,
  {
    enabledStatuses,
    enabledLineupFilters,
    eventSearchTerm,
    applicantSummaryByEvent,
  }
) => {
  return events.filter((event) => {
    const statusMatches = enabledStatuses.includes(getCalendarStatusClass(event.status));
    if (!statusMatches || !eventMatchesSearch(event, eventSearchTerm)) return false;

    return eventMatchesLineupFilters({
      event,
      enabledLineupFilters,
      applicantSummary: applicantSummaryByEvent[event._id],
    });
  });
};

const getFirstUpcomingCalendarEvent = (calendarEvents) => {
  const sorted = [...calendarEvents].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const now = Date.now();
  return sorted.find((calendarEvent) => new Date(calendarEvent.start).getTime() >= now) || sorted[0];
};

export const PostedEventsWorkspace = ({
  scope = "all",
  rosterId,
  rosterName,
  embedded = false,
  title = "posted events",
  compactFilters = false,
  hideToolsPanel = false,
  initialFiltersCollapsed,
  guidanceCopy,
  emptyCopy,
  onHeaderActionChange,
  // "calendar" | "list": bookings → dates shows the calendar, bookings → status the list; omit for the slide-in list
  inlineViewMode,
  onInlineViewModeChange,
} = {}) => {
  const medium = useMediaQuery("md");
  const isBookingsScope = scope === "bookings";
  const calendarPreferencesKey =
    rosterId
      ? `${POSTED_EVENTS_CALENDAR_PREFS_KEY}:roster:${rosterId}`
      : isBookingsScope
      ? `${POSTED_EVENTS_CALENDAR_PREFS_KEY}:bookings`
      : POSTED_EVENTS_CALENDAR_PREFS_KEY;
  const storedCalendarPreferences = useMemo(
    () => getPostedEventsCalendarPreferences(calendarPreferencesKey),
    [calendarPreferencesKey]
  );
  const [events, setEvents] = useState();
  const [calendarView, setCalendarView] = useState(
    storedCalendarPreferences.calendarView
  );
  const [calendarRange, setCalendarRange] = useState(null);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null);
  const [enabledStatuses, setEnabledStatuses] = useState(() =>
    storedCalendarPreferences.enabledStatuses.length > 0
      ? storedCalendarPreferences.enabledStatuses
      : DEFAULT_STATUS_FILTERS
  );
  const [condenseRejectedRecurring, setCondenseRejectedRecurring] = useState(
    storedCalendarPreferences.condenseRejectedRecurring
  );
  const [enabledLineupFilters, setEnabledLineupFilters] = useState(
    storedCalendarPreferences.enabledLineupFilters
  );
  const [filtersCollapsed, setFiltersCollapsed] = useState(
    initialFiltersCollapsed ??
      storedCalendarPreferences.filtersCollapsed ??
      !medium
  );
  const [eventSearchTerm, setEventSearchTerm] = useState("");
  const [applicantSummaryByEvent, setApplicantSummaryByEvent] = useState({});
  const [applicantLoadingByEvent, setApplicantLoadingByEvent] = useState({});
  const {
    userLocation,
    postedEventsViewMode: viewMode,
    setPostedEventsViewMode,
  } = usePortalContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listDrawerOpen, setListDrawerOpen] = useState(
    !isBookingsScope && storedCalendarPreferences.viewMode === "list"
  );
  const [drawerEditRequest, setDrawerEditRequest] = useState(null);
  const focusEventId = searchParams.get("focusEventId");
  const focusOccurrenceStart = searchParams.get("focusOccurrenceStart");
  const calendarFocusDate = focusOccurrenceStart || null;

  const filteredEvents = useMemo(
    () =>
      filterPostedEventsForCalendar(events || [], {
        enabledStatuses,
        enabledLineupFilters,
        eventSearchTerm,
        applicantSummaryByEvent,
      }),
    [
      events,
      enabledStatuses,
      enabledLineupFilters,
      eventSearchTerm,
      applicantSummaryByEvent,
    ]
  );

  const calendarEvents = useMemo(
    () =>
      expandPostedEventsForCalendar(filteredEvents, calendarRange || {}, {
        statusFilters: enabledStatuses,
        condenseRejectedRecurring,
      }),
    [filteredEvents, calendarRange, enabledStatuses, condenseRejectedRecurring]
  );

  const selectedParentEvent =
    selectedCalendarEvent?.extendedProps?.parentEvent || null;
  const selectedParentEventId = selectedParentEvent?._id;
  const selectedCalendarProps = selectedCalendarEvent?.extendedProps || {};
  const selectedSummaryKey = applicantSummaryKey(
    selectedParentEventId,
    selectedCalendarProps.occurrenceStart
  );
  const selectedApplicantSummary = selectedSummaryKey
    ? applicantSummaryByEvent[selectedSummaryKey]
    : null;
  const selectedLiveSlots = selectedApplicantSummary?.slotDetails;
  const selectedSlotSummary = selectedLiveSlots
    ? getSlotSummary({ performerSlots: selectedLiveSlots })
    : getSlotSummary(selectedParentEvent);
  const isApplicantsLoading = selectedSummaryKey
    ? applicantLoadingByEvent[selectedSummaryKey]
    : false;

  const fetchEvents = async () => {
    const params = {};
    if (rosterId) params.rosterId = rosterId;
    if (calendarRange?.start) params.from = new Date(calendarRange.start).toISOString();
    if (calendarRange?.end) params.to = new Date(calendarRange.end).toISOString();

    const { data } = await axiosInstance.get(`${backendUrl}/influencer/events`, {
      params,
    });
    if (data.error) {
      console.log(data.error);
    } else {
      setEvents(data.data);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [rosterId, calendarRange?.start, calendarRange?.end]);

  useEffect(() => {
    localStorage.setItem(
      calendarPreferencesKey,
      JSON.stringify({
        viewMode: isBookingsScope ? "calendar" : viewMode,
        calendarView,
        enabledStatuses,
        enabledLineupFilters,
        condenseRejectedRecurring,
        filtersCollapsed,
      })
    );
  }, [
    viewMode,
    calendarView,
    enabledStatuses,
    enabledLineupFilters,
    condenseRejectedRecurring,
    filtersCollapsed,
    calendarPreferencesKey,
    isBookingsScope,
  ]);

  useEffect(() => {
    if (!enabledLineupFilters.includes("pending-applicants")) return;

    const eventsNeedingApplicantCounts = (events || []).filter((event) => {
      if (!event?._id || !getSlotSummary(event).hasSlots) return false;
      if (!enabledStatuses.includes(getCalendarStatusClass(event.status))) return false;
      if (!eventMatchesSearch(event, eventSearchTerm)) return false;
      return !applicantSummaryByEvent[event._id] && !applicantLoadingByEvent[event._id];
    });

    if (eventsNeedingApplicantCounts.length === 0) return;

    const loadApplicantCounts = async () => {
      setApplicantLoadingByEvent((prev) =>
        eventsNeedingApplicantCounts.reduce(
          (acc, event) => ({ ...acc, [event._id]: true }),
          prev
        )
      );

      await Promise.all(
        eventsNeedingApplicantCounts.map(async (event) => {
          try {
            const data = await getEventApplicants(event._id);
            setApplicantSummaryByEvent((prev) => ({
              ...prev,
              [event._id]: summarizeApplicants(data.slots || []),
            }));
          } catch (error) {
            console.log(error);
            setApplicantSummaryByEvent((prev) => ({
              ...prev,
              [event._id]: { error: "unable to load applicant counts" },
            }));
          } finally {
            setApplicantLoadingByEvent((prev) => ({ ...prev, [event._id]: false }));
          }
        })
      );
    };

    loadApplicantCounts();
  }, [
    events,
    enabledLineupFilters,
    enabledStatuses,
    eventSearchTerm,
    applicantSummaryByEvent,
    applicantLoadingByEvent,
  ]);

  const cancelEvent = async (event) => {
    try {
      const result = await modal(({ proceed }) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--inyc-space-20)",
          }}
        >
          <div>canceling an event request is an irreversible action</div>
          <div
            className="whitespace-nowarp"
            style={{ fontFamily: "Primary-Medium" }}
          >
            are you sure you want to continue ?
          </div>
          <div
            style={{
              marginTop: "var(--inyc-space-20)",
              display: "flex",
              gap: "var(--inyc-space-20)",
              justifyContent: "center",
            }}
          >
            <Button onClick={() => proceed(true)}>Yes</Button>
            <Button onClick={() => proceed(false)}>No</Button>
          </div>
        </div>
      ));

      if (result) {
        try {
          const { data } = await axiosInstance.post(
            `${backendUrl}/influencer/cancelEvent/${event._id}`
          );
          if (data.error) {
            console.log(data.error);
            return false;
          } else {
            fetchEvents();
            return true;
          }
        } catch (error) {
          console.log(error);
          return false;
        }
      }
    } catch (error) {
      console.log(error);
    }
    return false;
  };

  const openEventDetails = (event) => {
    navigate(`/?location=${event._id}`);
  };

  const loadApplicantSummary = async (event, occurrenceStart) => {
    if (!event?._id || !getSlotSummary(event).hasSlots) return;
    const summaryKey = applicantSummaryKey(event._id, occurrenceStart);
    if (applicantSummaryByEvent[summaryKey] || applicantLoadingByEvent[summaryKey]) return;

    setApplicantLoadingByEvent((prev) => ({ ...prev, [summaryKey]: true }));
    try {
      const data = await getEventApplicants(event._id, occurrenceStart);
      setApplicantSummaryByEvent((prev) => ({
        ...prev,
        [summaryKey]: summarizeApplicants(data.slots || []),
      }));
    } catch (error) {
      console.log(error);
      setApplicantSummaryByEvent((prev) => ({
        ...prev,
        [summaryKey]: { error: "unable to load applicant counts" },
      }));
    } finally {
      setApplicantLoadingByEvent((prev) => ({ ...prev, [summaryKey]: false }));
    }
  };

  const handleCalendarEventClick = (calendarEvent) => {
    setSelectedCalendarEvent(calendarEvent);
    loadApplicantSummary(
      calendarEvent.extendedProps?.parentEvent,
      calendarEvent.extendedProps?.occurrenceStart
    );
  };

  useEffect(() => {
    if (!focusEventId || calendarEvents.length === 0) return;
    const focusTime = focusOccurrenceStart
      ? new Date(focusOccurrenceStart).getTime()
      : null;
    const matchingEvent = focusEventId === "first"
      ? getFirstUpcomingCalendarEvent(calendarEvents)
      : calendarEvents.find((calendarEvent) => {
          const props = calendarEvent.extendedProps || {};
          const parentId = props.parentEvent?._id || props.parentEvent?.id;
          if (String(parentId) !== String(focusEventId)) return false;
          if (!focusTime) return true;
          const occurrenceTime = new Date(
            props.occurrenceStart || calendarEvent.start
          ).getTime();
          return occurrenceTime === focusTime;
        });
    if (!matchingEvent) return;

    handleCalendarEventClick(matchingEvent);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("focusEventId");
      next.delete("focusOccurrenceStart");
      return next;
    }, { replace: true });
  }, [
    focusEventId,
    focusOccurrenceStart,
    calendarEvents,
    setSearchParams,
  ]);

  const closeCalendarPanel = () => {
    setSelectedCalendarEvent(null);
  };

  const openListDrawer = () => {
    closeCalendarPanel();
    setListDrawerOpen(true);
    setPostedEventsViewMode("list");
  };

  const closeListDrawer = () => {
    setListDrawerOpen(false);
    setPostedEventsViewMode("calendar");
  };

  const editEvent = (event) => {
    // editing happens in the calendar's panel, so leave the inline list
    if (inlineViewMode) onInlineViewModeChange?.("calendar");
    setListDrawerOpen(false);
    setPostedEventsViewMode("calendar");
    setDrawerEditRequest({ event, requestId: Date.now() });
  };

  useEffect(() => {
    if (isBookingsScope) return;
    if (viewMode === "list") {
      openListDrawer();
    } else {
      setListDrawerOpen(false);
    }
  }, [viewMode, isBookingsScope]);

  useEffect(() => {
    if (!onHeaderActionChange) return undefined;

    onHeaderActionChange({
      label: `list${filteredEvents.length > 0 ? ` (${filteredEvents.length})` : ""}`,
      onClick: openListDrawer,
    });

    return () => onHeaderActionChange(null);
  }, [onHeaderActionChange, filteredEvents.length]);

  const toggleStatusFilter = (statusId) => {
    setEnabledStatuses((prev) => {
      if (prev.includes(statusId) && prev.length === 1) return prev;
      return prev.includes(statusId)
        ? prev.filter((id) => id !== statusId)
        : [...prev, statusId];
    });
  };

  const toggleLineupFilter = (filterId) => {
    setEnabledLineupFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((id) => id !== filterId)
        : [...prev, filterId]
    );
  };

  const statusFilterCount =
    enabledStatuses.length === DEFAULT_STATUS_FILTERS.length
      ? 0
      : enabledStatuses.length;
  const activeFilterCount =
    statusFilterCount +
    enabledLineupFilters.length +
    (condenseRejectedRecurring !== DEFAULT_CONDENSE_REJECTED_RECURRING ? 1 : 0) +
    (eventSearchTerm.trim() ? 1 : 0);
  const hasCustomFilters =
    enabledStatuses.length !== DEFAULT_STATUS_FILTERS.length ||
    enabledLineupFilters.length > 0 ||
    condenseRejectedRecurring !== DEFAULT_CONDENSE_REJECTED_RECURRING ||
    eventSearchTerm.trim().length > 0;
  const hasNoCalendarMatches = calendarEvents.length === 0;

  const clearCalendarFilters = () => {
    setEnabledStatuses(DEFAULT_STATUS_FILTERS);
    setEnabledLineupFilters([]);
    setCondenseRejectedRecurring(DEFAULT_CONDENSE_REJECTED_RECURRING);
    setEventSearchTerm("");
    closeCalendarPanel();
  };

  const applyNeedsAttentionPreset = () => {
    setEnabledStatuses(["active", "awaiting-review"]);
    setEnabledLineupFilters(["open-slots", "pending-applicants"]);
    setCondenseRejectedRecurring(DEFAULT_CONDENSE_REJECTED_RECURRING);
    setEventSearchTerm("");
    closeCalendarPanel();
  };

  const getActiveFilterSummary = () => {
    const statusSummary =
      enabledStatuses.length === DEFAULT_STATUS_FILTERS.length
        ? "all statuses"
        : STATUS_FILTERS.filter((status) => enabledStatuses.includes(status.id))
            .map((status) => status.label)
            .join(", ");
    const lineupSummary =
      enabledLineupFilters.length > 0
        ? LINEUP_FILTERS.filter((filter) => enabledLineupFilters.includes(filter.id))
            .map((filter) => filter.label)
            .join(", ")
        : "all lineups";
    const searchSummary = eventSearchTerm.trim()
      ? `search: ${eventSearchTerm.trim()}`
      : "";

    return [statusSummary, lineupSummary, searchSummary].filter(Boolean).join(" | ");
  };

  const activeFilterSummary = getActiveFilterSummary();

  const getFilterButtonClass = ({ selected, tone = "default" }) => {
    if (!selected) {
      return "border-[var(--inyc-input-border-color)] bg-transparent text-[var(--inyc-primary-text-color-light)] opacity-65 hover:opacity-100";
    }

    if (tone === "rejected") {
      return "border-danger-line bg-danger-soft text-danger shadow-[inset_0_0_0_1px_rgba(239,68,68,0.12)]";
    }

    if (tone === "awaiting-review") {
      return "border-warning-line bg-warning-soft text-warning shadow-[inset_0_0_0_1px_rgba(245,158,11,0.16)]";
    }

    return "border-[var(--inyc-secondary)] bg-[var(--inyc-input-background-color)] text-[var(--inyc-primary-text-color)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]";
  };

  const handlePopupLineupChange = useCallback((summary) => {
    if (!selectedParentEventId) return;
    setApplicantSummaryByEvent((prev) => ({
      ...prev,
      [selectedParentEventId]: summary,
    }));
  }, [selectedParentEventId]);

  const handleListStatusFilterClick = (statusId) => {
    if (statusId === "all") {
      setEnabledStatuses(DEFAULT_STATUS_FILTERS);
      return;
    }

    setEnabledStatuses([statusId]);
  };

  return (
    <div
      style={{
        background: "var(--inyc-primary)",
        boxShadow: "var(--inyc-shadow-card)",
        ...(embedded ? panelStyles.embeddedCalendarPage : panelStyles.calendarPage),
      }}
    >
      {!embedded && <PortalTitle>{title}</PortalTitle>}

      {/* inline mode (bookings → dates): hint above the calendar, lined up under "dates" */}
      {inlineViewMode === "calendar" && guidanceCopy && (
        <div className="mb-kit-12 pl-kit-16 font-['Primary-Medium'] text-kit-base lowercase text-[var(--inyc-primary-text-color)]">
          {guidanceCopy}
        </div>
      )}

      {inlineViewMode === "list" ? (
        <PostedEventsListDrawer
          inline
          isOpen
          title="bookings list"
          emptyLabel="no booking events yet"
          showCount={false}
          events={events || []}
          filteredEvents={filteredEvents}
          enabledStatuses={enabledStatuses}
          defaultStatusFilters={DEFAULT_STATUS_FILTERS}
          onStatusFilterClick={handleListStatusFilterClick}
          onEditEvent={editEvent}
          onCancelEvent={cancelEvent}
          onOpenEvent={openEventDetails}
        />
      ) : (
        <PostedEventsCalendar
          scope={scope}
          rosterId={rosterId}
          rosterName={rosterName}
          calendarEvents={calendarEvents}
          calendarView={calendarView}
          setCalendarRange={setCalendarRange}
          setCalendarView={setCalendarView}
          hasNoCalendarMatches={hasNoCalendarMatches}
          filtersCollapsed={filtersCollapsed}
          setFiltersCollapsed={setFiltersCollapsed}
          activeFilterCount={activeFilterCount}
          activeFilterSummary={activeFilterSummary}
          applyNeedsAttentionPreset={applyNeedsAttentionPreset}
          hasCustomFilters={hasCustomFilters}
          clearCalendarFilters={clearCalendarFilters}
          statusFilters={STATUS_FILTERS}
          enabledStatuses={enabledStatuses}
          toggleStatusFilter={toggleStatusFilter}
          getFilterButtonClass={getFilterButtonClass}
          lineupFilters={LINEUP_FILTERS}
          enabledLineupFilters={enabledLineupFilters}
          toggleLineupFilter={toggleLineupFilter}
          applicantLoadingByEvent={applicantLoadingByEvent}
          condenseRejectedRecurring={condenseRejectedRecurring}
          setCondenseRejectedRecurring={setCondenseRejectedRecurring}
          eventSearchTerm={eventSearchTerm}
          setEventSearchTerm={setEventSearchTerm}
          selectedParentEvent={selectedParentEvent}
          selectedOccurrenceDisplayStart={selectedCalendarEvent?.start}
          selectedOccurrenceDisplayEnd={selectedCalendarEvent?.end}
          selectedCalendarProps={selectedCalendarProps}
          selectedSlotSummary={selectedSlotSummary}
          selectedApplicantSummary={selectedApplicantSummary}
          isApplicantsLoading={isApplicantsLoading}
          handleCalendarEventClick={handleCalendarEventClick}
          calendarFocusDate={calendarFocusDate}
          closeCalendarPanel={closeCalendarPanel}
          openEventDetails={openEventDetails}
          cancelEvent={cancelEvent}
          handlePopupLineupChange={handlePopupLineupChange}
          userLocation={userLocation}
          onPostedEventCreated={fetchEvents}
          onOpenList={inlineViewMode ? () => onInlineViewModeChange?.("list") : openListDrawer}
          listEventCount={filteredEvents.length}
          editingEventRequest={drawerEditRequest}
          compactFilters={compactFilters}
          hideToolsPanel={hideToolsPanel}
          guidanceCopy={guidanceCopy}
          hideGuidance={Boolean(inlineViewMode)}
          emptyCopy={emptyCopy}
        />
      )}
      {!inlineViewMode && (
        <PostedEventsListDrawer
          isOpen={listDrawerOpen}
          onClose={closeListDrawer}
          events={events || []}
          filteredEvents={filteredEvents}
          enabledStatuses={enabledStatuses}
          defaultStatusFilters={DEFAULT_STATUS_FILTERS}
          onStatusFilterClick={handleListStatusFilterClick}
          onEditEvent={editEvent}
          onCancelEvent={cancelEvent}
          onOpenEvent={openEventDetails}
        />
      )}
    </div>
  );
};

const PostedEvents = () => <PostedEventsWorkspace />;

const panelStyles = {
  calendarPage: {
    width: "clamp(100%, calc(100vw - 8rem), 1400px)",
    marginLeft: "50%",
    transform: "translateX(-50%)",
  },
  embeddedCalendarPage: {
    width: "100%",
  },
};

export default PostedEvents;
